/**
 * useScreenOrientation
 *
 * Debounced screen orientation detection for video calls.
 * Follows FaceTime-style UX: treat orientation as a stable state change,
 * not continuous—avoids rapid flipping when the user adjusts grip.
 *
 * - Debounces 300–500ms before committing orientation
 * - Uses screen.orientation API when available (portrait-primary, landscape-secondary, etc.)
 * - Fallbacks for older browsers (window.orientation, matchMedia, resize)
 * - Supports 180° within same orientation (portrait-up vs portrait-down)
 * - Works on mobile and desktop
 */

import { useState, useEffect, useRef, useCallback } from 'react';

/** Orientation angle in degrees: 0, 90, 180, or 270 */
export type OrientationAngle = 0 | 90 | 180 | 270;

/** Orientation type from screen.orientation API */
export type OrientationType =
  | 'portrait-primary'
  | 'portrait-secondary'
  | 'landscape-primary'
  | 'landscape-secondary'
  | 'portrait'
  | 'landscape';

export interface ScreenOrientationState {
  /** Angle in degrees (0, 90, 180, 270) */
  angle: OrientationAngle;
  /** Orientation type when available */
  type: OrientationType;
  /** True when in landscape */
  isLandscape: boolean;
  /** True when in portrait */
  isPortrait: boolean;
  /** True when orientation has been stable for debounce period (committed) */
  isStable: boolean;
}

const DEBOUNCE_MS = 400;

/**
 * Map screen.orientation.type to angle
 */
function typeToAngle(type: string): OrientationAngle {
  switch (type) {
    case 'portrait-primary':
      return 0;
    case 'landscape-primary':
      return 90;
    case 'portrait-secondary':
      return 180;
    case 'landscape-secondary':
      return 270;
    default:
      return 0;
  }
}

const VALID_TYPES: OrientationType[] = [
  'portrait-primary',
  'portrait-secondary',
  'landscape-primary',
  'landscape-secondary',
];

/**
 * Get orientation from screen.orientation API (preferred)
 */
function getScreenOrientation(): { angle: OrientationAngle; type: OrientationType } | null {
  if (typeof screen === 'undefined' || !(screen as any).orientation) return null;
  try {
    const orient = (screen as any).orientation;
    const type = VALID_TYPES.includes(orient.type) ? orient.type : 'portrait-primary';
    const angle = orient.angle ?? 0;
    const normalizedAngle = [0, 90, 180, 270].includes(angle)
      ? (angle as OrientationAngle)
      : typeToAngle(type);
    return { angle: normalizedAngle, type };
  } catch {
    return null;
  }
}

/**
 * Get orientation from deprecated window.orientation (iOS Safari fallback)
 */
function getWindowOrientation(): { angle: OrientationAngle; type: OrientationType } | null {
  const win = typeof window !== 'undefined' ? (window as any) : null;
  if (!win || win.orientation === undefined) return null;
  const o = win.orientation;
  // -90 = landscape-secondary, 0 = portrait, 90 = landscape-primary, 180 = portrait-secondary
  if (o === -90) return { angle: 270, type: 'landscape-secondary' };
  if (o === 0) return { angle: 0, type: 'portrait-primary' };
  if (o === 90) return { angle: 90, type: 'landscape-primary' };
  if (o === 180) return { angle: 180, type: 'portrait-secondary' };
  return { angle: 0, type: 'portrait-primary' };
}

/**
 * Get orientation from window dimensions (fallback when APIs unavailable)
 */
function getResizeOrientation(): { angle: OrientationAngle; type: OrientationType } {
  const isLandscape = typeof window !== 'undefined' && window.innerWidth > window.innerHeight;
  return isLandscape
    ? { angle: 90, type: 'landscape-primary' }
    : { angle: 0, type: 'portrait-primary' };
}

/**
 * Get orientation from Device Orientation API (accelerometer).
 * Works when screen is locked - detects physical device tilt.
 * gamma: left-to-right tilt (-90 to 90).
 *   > 45  → right side down → landscape-primary
 *   < -45 → left side down  → landscape-secondary
 *   else  → portrait
 */
function getPhysicalOrientationFromGamma(gamma: number | null): { angle: OrientationAngle; type: OrientationType } | null {
  if (gamma === null || gamma === undefined) return null;
  if (gamma > 45) return { angle: 90, type: 'landscape-primary' };
  if (gamma < -45) return { angle: 270, type: 'landscape-secondary' };
  return { angle: 0, type: 'portrait-primary' };
}

/**
 * Get current orientation using best available method
 */
function getCurrentOrientation(physicalGamma?: number | null): { angle: OrientationAngle; type: OrientationType } {
  // When physicalGamma is provided (from deviceorientation), prefer it - works with screen lock
  const physical = physicalGamma != null ? getPhysicalOrientationFromGamma(physicalGamma) : null;
  if (physical) return physical;

  return (
    getScreenOrientation() ??
    getWindowOrientation() ??
    getResizeOrientation()
  );
}

interface UseScreenOrientationOptions {
  /** Debounce delay in ms before committing orientation (default 400) */
  debounceMs?: number;
  /** Whether the hook is active (e.g. only during a call) */
  enabled?: boolean;
  /** Use Device Orientation API (works when screen locked to portrait, e.g. for guests) */
  usePhysicalOrientation?: boolean;
}

/**
 * Hook to detect screen orientation with debouncing.
 * Returns stable orientation only after it has been consistent for debounceMs.
 */
export function useScreenOrientation(
  options: UseScreenOrientationOptions = {}
): ScreenOrientationState {
  const { debounceMs = DEBOUNCE_MS, enabled = true, usePhysicalOrientation = false } = options;

  const [state, setState] = useState<ScreenOrientationState>(() => {
    const { angle, type } = getCurrentOrientation();
    return {
      angle,
      type,
      isLandscape: angle === 90 || angle === 270,
      isPortrait: angle === 0 || angle === 180,
      isStable: true,
    };
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ angle: OrientationAngle; type: OrientationType } | null>(null);
  const physicalGammaRef = useRef<number | null>(null);

  const commitOrientation = useCallback((angle: OrientationAngle, type: OrientationType) => {
    setState({
      angle,
      type,
      isLandscape: angle === 90 || angle === 270,
      isPortrait: angle === 0 || angle === 180,
      isStable: true,
    });
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleChange = (gamma?: number | null) => {
      const next = getCurrentOrientation(gamma ?? physicalGammaRef.current);

      // Clear existing debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      // Mark as unstable while debouncing
      setState((prev) => ({ ...prev, isStable: false }));
      pendingRef.current = next;

      debounceRef.current = setTimeout(() => {
        if (pendingRef.current) {
          commitOrientation(pendingRef.current.angle, pendingRef.current.type);
        }
        debounceRef.current = null;
      }, debounceMs);
    };

    // Device Orientation API - works when screen is locked (for guests)
    // Uses gamma (left-to-right tilt) to detect landscape vs portrait
    let deviceOrientationCleanup: (() => void) | null = null;
    if (usePhysicalOrientation && typeof window !== 'undefined') {
      const handleDeviceOrientation = (e: DeviceOrientationEvent) => {
        const gamma = e.gamma;
        if (gamma != null) {
          physicalGammaRef.current = gamma;
          handleChange(gamma);
        }
      };
      window.addEventListener('deviceorientation', handleDeviceOrientation);
      deviceOrientationCleanup = () => window.removeEventListener('deviceorientation', handleDeviceOrientation);
      // NOTE: iOS 13+ permission request must happen during a user gesture.
      // It is handled in VideoCallRoom.tsx on first user interaction, not here.
    }

    // Initial read
    const initial = getCurrentOrientation(usePhysicalOrientation ? physicalGammaRef.current : undefined);
    setState((prev) => ({
      ...prev,
      ...initial,
      isLandscape: initial.angle === 90 || initial.angle === 270,
      isPortrait: initial.angle === 0 || initial.angle === 180,
      isStable: true,
    }));

    // Screen APIs — ALWAYS listen as a fallback, even when usePhysicalOrientation is true.
    // On iOS, deviceorientation events only fire after the user grants permission.
    // Until that happens (or if denied), screen/resize events are the only orientation signal.
    // Once deviceorientation starts flowing, getCurrentOrientation(gamma) takes priority.
    const orient = typeof screen !== 'undefined' ? (screen as any).orientation : null;
    const handleScreenChange = () => handleChange();

    if (orient?.addEventListener) {
      orient.addEventListener('change', handleScreenChange);
    }
    window.addEventListener('orientationchange', handleScreenChange);
    window.addEventListener('resize', handleScreenChange);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      deviceOrientationCleanup?.();
      if (orient?.removeEventListener) {
        orient.removeEventListener('change', handleScreenChange);
      }
      window.removeEventListener('orientationchange', handleScreenChange);
      window.removeEventListener('resize', handleScreenChange);
    };
  }, [enabled, debounceMs, commitOrientation, usePhysicalOrientation]);

  return state;
}
